// ** Redux Imports
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorMessage } from '@/utils/errors';
import { isNShowcaseLocal } from '@/utils/nShowcase';
import type { BlogAuthor, BlogAuthorLink } from '@/nova/mock-data/types';

import { RootState } from '../index';

const DEBUG = false;
const apiUrl = window.config.userApiUrl + '/profile/preferences';

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

/* ---------- API response shapes ---------- */

/**
 * Free-form bag of user preferences. The backend stores whatever key/value pairs
 * are POSTed (the curl example sends `{ "key": "value" }`), so this is kept as an
 * open record rather than a fixed schema — add typed accessors as concrete keys
 * settle. The GET response is assumed to be the flat object; the reducer does NOT
 * unwrap a `{ data: {...} }` envelope (a preference could legitimately be named
 * `data`) — see the thunk note below.
 */
export type UserPreferences = Record<string, unknown>;

/* ---------- typed preference namespaces ----------

The bag is free-form, but the app stores a handful of well-known objects under
reserved TOP-LEVEL keys. The backend merges POSTed keys SHALLOWLY
(`{ ...state, ...patch }`), so the rule for every writer is: when you change one
field of a namespace, POST the WHOLE namespace object — never a single nested
field — or sibling fields in that namespace would be dropped. Scalars
(`lastBrandId`) are POSTed on their own. Different namespaces never collide
because they are distinct top-level keys. */

export const PREFERENCE_KEYS = {
  /** Owner profile (everything EXCEPT email — email is auth-managed). */
  ownerProfile: 'ownerProfile',
  /** Communication preferences: a category × channel matrix. */
  comms: 'commsPrefs',
  /** Billing / withdrawal bank + KYC details. */
  billing: 'billing',
  /** The owner's last selected brand id (cross-device brand restore). */
  lastBrandId: 'lastBrandId',
  /** Studio appearance: theme mode (auto/light/dark) + accent. See
   *  `nova/theme/themeConfig.ts` (`AppearancePrefs`). Persisted whole. */
  appearance: 'appearance',
  /** The byline the owner last put on a blog article, reused as the default on
   *  every new one (`nova/pages/blog-editor`). A whole {@link BlogAuthor}, so it
   *  is POSTed entire. Account-scoped rather than per-brand: it is a person, and
   *  an owner running several casinos signs their writing the same way in each. */
  blogAuthor: 'blogAuthor',
  /** Preferred UI language (ISO code, e.g. 'en' / 'mx'). A scalar (like
   *  `lastBrandId`) so the choice follows the account across devices, on top of the
   *  `language` localStorage that already persists it per-browser. */
  language: 'language',
  /** Privacy / tracking consent (marketing cookies). Gates the runtime GA + Facebook
   *  Pixel loaders (see `src/utils/analytics`); mirrored to the browser-scoped
   *  `ows-tracking-consent` localStorage record for the pre-auth / first-paint gate. */
  privacy: 'privacy',
  /** Ad-attribution first-touch snapshot captured on the marketing site and handed off via
   *  `startProperties.tracking` (ad-network click-ids + `utm_*` + first-touch metadata). Set ONCE
   *  per account (immutable first-touch) for a later offline / server-side conversion upload. See
   *  `nova/pages/configurator-{v3,v0}/lib/startProperties.ts` + `docs/app-startproperties-tracking.md`. */
  tracking: 'tracking',
  /** Newton showcase switch: `true` swaps the Whitelabels.com marks for Newton's
   *  (see `utils/nShowcase`). Set outside the app; the front-end only reads it.
   *  Kept snake_case because that is the stored key. */
  nShowcase: 'n_showcase',
  /** FTUX - Getting Started's intro: where the owner stands in Lumio's hello and the menu's
   *  map, once per owner (`nova/features/ftux/store/persistence.ts`). Each brand's run of the
   *  pieces has its own key, `FTUX_RUN_KEY_PREFIX` + the brand id. POSTed whole, with a fresh
   *  `updatedAt`. */
  ftuxIntro: 'ftuxIntro',
} as const;

/** FTUX - Getting Started keeps one run per brand, each under its own top-level key
 *  (`ftux:<brandId>`), so two tabs on two brands never write over each other's progress. */
export const FTUX_RUN_KEY_PREFIX = 'ftux:';

/** Owner profile fields persisted to preferences (email lives in auth, not here). */
export interface OwnerProfilePrefs {
  firstName: string;
  lastName: string;
  company: string;
  address: string;
}

/** Communication channels a category can be delivered on. */
export type CommChannel = 'email' | 'sms' | 'push';
/** Communication categories the owner can opt in/out of. `security` is required. */
export type CommCategory = 'product' | 'offers' | 'security' | 'newsletter';
export type CommChannelPrefs = Record<CommChannel, boolean>;
/** Category → per-channel opt-in matrix. */
export type CommsPrefs = Record<CommCategory, CommChannelPrefs>;

/**
 * Privacy / tracking consent. A single `marketing` switch (the owner-hub mirror of the
 * marketing site's cookie choice) gates BOTH Google Analytics and the Facebook Pixel — they
 * load at runtime only when it's `true` (see `src/utils/analytics` + `src/components/
 * analytics-consent`). Seeded from the `startProperties` hand-off and editable in
 * Settings › Privacy.
 */
export interface PrivacyPrefs {
  marketing: boolean;
}

/**
 * A first-touch ad-attribution snapshot: the sanitized `startProperties.tracking` object handed off
 * by the marketing site (ad-network click-ids + `utm_*` + first-touch metadata), plus the onboarding
 * `flow` variant (`v0` / `v3`) folded in from the top-level hand-off. A flat, SPARSE bag — only
 * params that were actually present on the visitor's URL are stored, so it has no fixed schema
 * (values are strings; `_first_ts` / `_last_ts` are numbers). Written ONCE per account and
 * never overwritten (immutable first-touch), so a returning visitor can't clobber the original
 * attribution. Used later for offline / server-side conversion upload to the ad networks; it is
 * analytics data only — NEVER an authorization / pricing / entitlement input. Seeded + validated in
 * `nova/pages/configurator-{v3,v0}/lib/startProperties.ts`; see `docs/app-startproperties-tracking.md`.
 */
export type TrackingPrefs = Record<string, string | number>;

/** Whether the payout beneficiary is a private individual or a business/company. */
export type BillingAccountType = '' | 'individual' | 'business';

/** Legal-entity classification for a BUSINESS billing/withdrawal beneficiary. */
export type BusinessType = '' | 'sole_proprietor' | 'partnership' | 'llc' | 'corp' | 'other';

/**
 * Bank + identity details an owner submits so we can pay out (withdraw) from
 * their main wallet. No payout endpoint exists yet, so these are stored as
 * preferences and will feed a future withdrawal-submission API.
 *
 * The owner configures a PAYOUT METHOD before any withdrawal: a bank account
 * (`bankEnabled`) and/or a crypto wallet (`cryptoEnabled`), one or both. The KYC
 * identity (name / business / tax / DOB / address) is always required regardless
 * of method. The bank field set covers a SEPA (EU/IBAN) or international wire and
 * adapts to whether the beneficiary is an INDIVIDUAL (person name + DOB) or a
 * BUSINESS (legal entity + type + VAT + beneficial owner). `accountType` drives
 * which identity fields apply (see the UI + `validateBilling`).
 */
export interface BillingPrefs {
  /** Individual vs business; drives which identity fields are required/shown. */
  accountType: BillingAccountType;
  // Payout method(s): the owner can set up a bank account, a crypto wallet, or
  // both. At least one must be enabled (see `validateBilling`).
  bankEnabled: boolean;
  cryptoEnabled: boolean;
  // When more than one rail is configured, the owner's chosen payout destination
  // (set from the Wallet). Empty falls back to the first configured rail.
  preferredPayoutMethod: '' | 'bank' | 'crypto';
  // Account holder (individual) / beneficiary name.
  accountHolderFirstName: string;
  accountHolderLastName: string;
  // Business identity (business accounts only).
  businessLegalName: string;
  businessType: BusinessType;
  // Bank account. Which of these apply is country-specific (see `bankRules.ts`
  // `bankFieldsForCountry`, which the form + validator both consume). The EU /
  // SEPA corridor needs only IBAN + BIC; an international wire additionally needs
  // the bank's address and, on some corridors, an intermediary bank.
  bankCountry: string; // ISO 3166-1 alpha-2
  bankName: string;
  bankAddress: string; // international wires: the beneficiary bank's address
  currency: string; // ISO 4217 (defaults to EUR for SEPA countries)
  iban: string; // IBAN countries (~88)
  bicSwift: string; // SWIFT/BIC: global cross-border identifier
  accountNumber: string; // non-IBAN countries
  sortCode: string; // UK
  routingNumber: string; // US (ABA)
  ifsc: string; // India
  bsb: string; // Australia
  institutionNumber: string; // Canada
  transitNumber: string; // Canada
  clabe: string; // Mexico
  cnapsCode: string; // China
  bankCode: string; // generic local bank code (JP/KR/BR/NG/HK/SG/RU/…)
  branchCode: string; // generic local branch code (JP/BR/ZA/HK/SG/…)
  // Intermediary / correspondent bank (only needed on certain corridors).
  intermediaryBankSwift: string;
  intermediaryBankAccount: string;
  // Crypto wallet payout. Network drives which asset list + whether a memo /
  // destination tag is required (see `cryptoRules.ts`).
  walletAddress: string;
  cryptoNetwork: string; // chain id (see CRYPTO_NETWORKS in cryptoRules.ts)
  cryptoAsset: string; // token/asset code (BTC, ETH, USDT, …)
  cryptoMemo: string; // destination tag / memo, required for some chains (XRP, XLM)
  cryptoLabel: string; // optional nickname for this wallet
  // Tax & beneficial owner (KYC/AML).
  taxId: string;
  vatNumber: string;
  beneficialOwnerFirstName: string;
  beneficialOwnerLastName: string;
  dateOfBirth: string; // YYYY-MM-DD
  // Billing address.
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string; // ISO 3166-1 alpha-2 (may differ from bankCountry)
}

/** A blank owner profile (the non-email fields persisted to preferences). */
export const defaultOwnerProfilePrefs = (): OwnerProfilePrefs => ({
  firstName: '',
  lastName: '',
  company: '',
  address: '',
});

/** Tracking is ON by default; only an explicit opt-out (marketing site or Settings) turns it off. */
export const defaultPrivacyPrefs = (): PrivacyPrefs => ({
  marketing: true,
});

/** All categories start opted-out except the required `security` notifications. */
export const defaultCommsPrefs = (): CommsPrefs => ({
  product: { email: false, sms: false, push: false },
  offers: { email: false, sms: false, push: false },
  security: { email: true, sms: false, push: false },
  newsletter: { email: false, sms: false, push: false },
});

/** A fully-blank billing record (every field present, so the form is controlled).
 *  Bank is the default payout method (matches every pre-payout-method record,
 *  which only ever held bank details); crypto is opt-in. */
export const defaultBillingPrefs = (): BillingPrefs => ({
  accountType: '',
  bankEnabled: true,
  cryptoEnabled: false,
  preferredPayoutMethod: '',
  accountHolderFirstName: '',
  accountHolderLastName: '',
  businessLegalName: '',
  businessType: '',
  bankCountry: '',
  bankName: '',
  bankAddress: '',
  currency: '',
  iban: '',
  bicSwift: '',
  accountNumber: '',
  sortCode: '',
  routingNumber: '',
  ifsc: '',
  bsb: '',
  institutionNumber: '',
  transitNumber: '',
  clabe: '',
  cnapsCode: '',
  bankCode: '',
  branchCode: '',
  intermediaryBankSwift: '',
  intermediaryBankAccount: '',
  walletAddress: '',
  cryptoNetwork: '',
  cryptoAsset: '',
  cryptoMemo: '',
  cryptoLabel: '',
  taxId: '',
  vatNumber: '',
  beneficialOwnerFirstName: '',
  beneficialOwnerLastName: '',
  dateOfBirth: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: '',
});

/* ---------- schema hygiene (keep the free-form bag from bloating) ----------

The backend MERGES POSTed keys and has NO hard delete (verified against the API:
PUT / PATCH / DELETE all 404; POSTing `null` only tombstones the key). Two rules
keep the bag lean:
  1. WRITE only the CURRENT schema's fields. `pickShape(saved, defaultX())` drops
     any field we've since removed, so re-saving a namespace purges it (a POST
     replaces the whole namespace value). The settings form hooks read AND write
     through `pickShape`.
  2. Retire a whole top-level key by listing it in `DEPRECATED_PREFERENCE_KEYS`;
     it's null-tombstoned once on load (the closest thing to delete the API
     allows). UNKNOWN keys are never touched — only surfaced — so a newer client's
     keys can't be clobbered by an older one. `prefsSchemaVersion` stamps a baseline
     for future rename/move migrations. */

/**
 * Project `saved` onto the shape of `defaults`: keep only keys that exist on
 * `defaults`, taking the saved value when it's present AND the same primitive
 * type, otherwise the default. Drops unknown/removed keys and type-mismatched
 * values. For FLAT namespaces (ownerProfile, billing) — the comms matrix projects
 * itself (it reads known category/channel cells only).
 */
export const pickShape = <T extends object>(saved: unknown, defaults: T): T => {
  const out: T = { ...defaults };
  if (saved === null || typeof saved !== 'object') return out;
  const src = saved as Record<string, unknown>;
  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    const value = src[key as string];
    if (value !== undefined && typeof value === typeof defaults[key]) {
      out[key] = value as T[keyof T];
    }
  });
  return out;
};

/**
 * Read a saved blog byline back out of the bag, or `null` when there isn't a
 * usable one.
 *
 * `pickShape` cannot police this namespace: `links` is an array, `typeof []` is
 * `'object'`, so a record whose links came back as a plain object (or as `null`
 * from a tombstone) would pass its type gate and reach the editor's form as
 * something the byline UI would then try to `.map()`. Every field is re-checked
 * here instead.
 *
 * A record with no name resolves to `null` on purpose: a nameless byline is no
 * byline at all everywhere else in the blog (`authorToApi` sends it as `null`),
 * so it is not a default worth prefilling a new article with.
 */
export const normalizeBlogAuthorPrefs = (saved: unknown): BlogAuthor | null => {
  if (saved === null || typeof saved !== 'object') return null;
  const src = saved as Record<string, unknown>;
  const text = (key: string): string => (typeof src[key] === 'string' ? (src[key] as string) : '');
  const name = text('name');
  if (!name.trim()) return null;
  const links: BlogAuthorLink[] = Array.isArray(src.links)
    ? src.links
        .filter((link): link is Record<string, unknown> => link !== null && typeof link === 'object')
        .map((link) => ({
          label: typeof link.label === 'string' ? link.label : '',
          url: typeof link.url === 'string' ? link.url : '',
        }))
    : [];
  return { name, role: text('role'), bio: text('bio'), avatarUrl: text('avatarUrl'), url: text('url'), links };
};

/**
 * Whether a byline is already stored exactly as given, so a save can skip the
 * POST. Compared field by field rather than through a JSON round-trip, whose
 * verdict would also depend on key order: the stored one is rebuilt by
 * {@link normalizeBlogAuthorPrefs} and the other comes off an article, so the
 * two are equal in value while nothing guarantees they are equal as text.
 */
export const sameBlogAuthorPrefs = (a: BlogAuthor | undefined, b: BlogAuthor | null): boolean => {
  if (!a || !b) return false;
  return (
    a.name === b.name &&
    a.role === b.role &&
    a.bio === b.bio &&
    a.avatarUrl === b.avatarUrl &&
    a.url === b.url &&
    a.links.length === b.links.length &&
    a.links.every((link, index) => link.label === b.links[index].label && link.url === b.links[index].url)
  );
};

/** Bumped when a one-time rename/retire migration needs to run per user. */
export const PREFS_SCHEMA_VERSION = 1;
export const PREFS_VERSION_KEY = 'prefsSchemaVersion';

/** Top-level keys the app actively owns. */
export const MANAGED_PREFERENCE_KEYS: ReadonlySet<string> = new Set<string>([
  ...Object.values(PREFERENCE_KEYS),
  PREFS_VERSION_KEY,
]);

/** Families of top-level keys the app owns, by prefix (one key per brand). */
export const MANAGED_PREFERENCE_PREFIXES: readonly string[] = [FTUX_RUN_KEY_PREFIX];

/** Top-level keys we've RETIRED — null-tombstoned once on load (no hard delete
 *  exists). Add a key here when you stop using a whole namespace. */
export const DEPRECATED_PREFERENCE_KEYS: readonly string[] = [
  // FTUX - Getting Started's per-owner run, replaced by `ftuxIntro` and one run per brand.
  'ftux',
];

/**
 * Plan the one-time cleanup to run after a fetch: a `patch` to POST
 * (null-tombstone retired keys + stamp the schema version) and any `unknownKeys`
 * present that nobody manages (surfaced so they get retired deliberately, never
 * auto-destroyed). `patch` is null when there's nothing to do, so it self-limits
 * and never re-POSTs once applied.
 */
export const planPrefsMigration = (
  preferences: UserPreferences,
): { patch: UserPreferences | null; unknownKeys: string[] } => {
  const patch: UserPreferences = {};
  DEPRECATED_PREFERENCE_KEYS.forEach((key) => {
    if (key in preferences && preferences[key] !== null) patch[key] = null;
  });
  if (preferences[PREFS_VERSION_KEY] !== PREFS_SCHEMA_VERSION) patch[PREFS_VERSION_KEY] = PREFS_SCHEMA_VERSION;
  const unknownKeys = Object.keys(preferences).filter(
    (key) =>
      !MANAGED_PREFERENCE_KEYS.has(key) &&
      !MANAGED_PREFERENCE_PREFIXES.some((prefix) => key.startsWith(prefix)) &&
      !DEPRECATED_PREFERENCE_KEYS.includes(key) &&
      preferences[key] !== null,
  );
  return { patch: Object.keys(patch).length > 0 ? patch : null, unknownKeys };
};

/* ---------- thunks ----------

Endpoints (require an `Authorization` header carrying the access token):
  GET  {userApiUrl}/profile/preferences          → current preferences
  POST {userApiUrl}/profile/preferences  { ... }  → merge/save preferences

The bag is free-form, so we intentionally do NOT sniff for a `{ data: {...} }`
envelope: a preference could legitimately be named `data`, and unwrapping it
would silently drop sibling keys. We assume GET returns the flat object — if the
backend wraps it, adjust `fetchUserPreferences.fulfilled` to read `payload.data`.
*/

/** GET result: the preferences bag plus the authenticated `user_id` it was loaded
 *  for, so a mid-session identity change (impersonation without logout) can detect
 *  prefs that belong to the PREVIOUS owner and discard them. */
type FetchPreferencesResult = { preferences: UserPreferences; forUserId: string | null };

export const fetchUserPreferences = createAsyncThunk<FetchPreferencesResult, void, GenericError>(
  'userPreferences/fetch',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const headers = getAuthHeaders(state);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.get(apiUrl, { headers });
      DEBUG && console.log('[UserPreferences Slice] fetch response', response);

      if (!isNil(response.data)) {
        // Tag the loaded prefs with the identity they belong to. A support agent can
        // switch the impersonated owner mid-session (magic link, no logout); tagging
        // lets the store detect another owner's prefs and re-fetch the current one's.
        return { preferences: response.data as UserPreferences, forUserId: state.authentication.user.user_id ?? null };
      }
      return rejectWithValue({ error: "Couldn't fetch user preferences" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/**
 * Save (merge) one or more preference keys. Resolves with the `requested` patch
 * so the reducer can merge it into state deterministically — the response
 * envelope is unconfirmed, so we treat what we sent as the source of truth.
 */
export const saveUserPreferences = createAsyncThunk<UserPreferences, UserPreferences, GenericError>(
  'userPreferences/save',
  async (preferences, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.post(apiUrl, preferences, { headers });
      DEBUG && console.log('[UserPreferences Slice] save response', response);

      return preferences;
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- slice state ---------- */

export interface UserPreferencesState {
  preferences: UserPreferences;

  /** The authenticated `user_id` the loaded `preferences` belong to (null until a
   *  successful fetch). Lets the store detect — and discard — another owner's prefs
   *  after a mid-session identity change (impersonation without an explicit logout). */
  loadedForUserId: string | null;

  /** GET request status. */
  loading: boolean;
  loaded: boolean;
  error: string | null | undefined;

  /** POST request status. */
  saving: boolean;
  saveError: string | null | undefined;
}

const initialState: UserPreferencesState = {
  preferences: {},
  loadedForUserId: null,

  loading: false,
  loaded: false,
  error: null,

  saving: false,
  saveError: null,
};

export const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    /**
     * Wipe preferences back to the empty initial state. Dispatched by the central
     * session-teardown listener (`store/index.ts`) on logout so one user's saved
     * profile / billing / brand never bleeds into the next session on a shared
     * browser. Mirrors `resetCasinoInstance`.
     */
    resetUserPreferences: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /* ----- GET /profile/preferences ----- */
      .addCase(fetchUserPreferences.pending, (state) => {
        state.loading = true;
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.preferences = action.payload?.preferences ?? {};
        state.loadedForUserId = action.payload?.forUserId ?? null;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload?.error;
      })

      /* ----- POST /profile/preferences ----- */
      .addCase(saveUserPreferences.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(saveUserPreferences.fulfilled, (state, action) => {
        state.saving = false;
        // Optimistically merge the saved patch so the store reflects the new
        // values without a re-fetch.
        state.preferences = {
          ...state.preferences,
          ...(action.payload ?? {}),
        };
      })
      .addCase(saveUserPreferences.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload?.error;
        // Surfaced via `saveError` for forms that show it; brand sync is fire-and-
        // forget (best-effort), so log under DEBUG to aid diagnosis without noise.
        DEBUG && console.warn('[UserPreferences Slice] save failed', action.payload?.error);
      });
  },
});

export const { resetUserPreferences } = userPreferencesSlice.actions;

/* ---------- selectors ---------- */

export const selectUserPreferences = (state: RootState): UserPreferences => state.userPreferences.preferences;

/**
 * True when prefs are loaded but belong to a DIFFERENT identity than the currently
 * authenticated user — i.e. they are another owner's data and must be discarded +
 * re-fetched. A support agent can sign in AS a new owner via a magic link without
 * logging out, which swaps the user but never runs the logout teardown; this is the
 * signal the identity-change listener (`store/index.ts`) keys on. A token refresh
 * keeps the same `user_id`, so it stays false on routine rotation.
 */
export const selectPreferencesStaleForUser = (state: RootState): boolean => {
  const userId = state.authentication.user.user_id;
  return state.userPreferences.loaded && userId != null && state.userPreferences.loadedForUserId !== userId;
};

/** The persisted owner profile, or `null` if nothing has been saved yet. */
export const selectOwnerProfilePrefs = (state: RootState): OwnerProfilePrefs | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.ownerProfile] as OwnerProfilePrefs | undefined) ?? null;

/** The persisted comms matrix, or `null` if nothing has been saved yet. */
export const selectCommsPrefs = (state: RootState): CommsPrefs | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.comms] as CommsPrefs | undefined) ?? null;

/** The persisted billing/withdrawal details, or `null` if nothing saved yet. */
export const selectBillingPrefs = (state: RootState): BillingPrefs | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.billing] as BillingPrefs | undefined) ?? null;

/** The persisted privacy / tracking consent, or `null` if nothing has been saved yet. */
export const selectPrivacyPrefs = (state: RootState): PrivacyPrefs | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.privacy] as PrivacyPrefs | undefined) ?? null;

/** The persisted first-touch ad-attribution snapshot, or `null` if none was captured. Immutable
 *  once set — see {@link TrackingPrefs}. */
export const selectTrackingPrefs = (state: RootState): TrackingPrefs | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.tracking] as TrackingPrefs | undefined) ?? null;

/** The persisted last-selected brand id, or `null`. */
export const selectLastBrandIdPref = (state: RootState): string | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.lastBrandId] as string | undefined) ?? null;

/** Whether the owner's `n_showcase` preference is on. Only a stored boolean `true` counts. */
export const selectNShowcasePref = (state: RootState): boolean =>
  state.userPreferences.preferences[PREFERENCE_KEYS.nShowcase] === true;

/** The browser-local `localStorage.n_showcase` switch, read once at load: it is
 *  documented as set-and-reload, and reading it here keeps the selector pure. */
const N_SHOWCASE_LOCAL = isNShowcaseLocal();

/** Whether the Newton showcase is on: the preference above, or the browser-local
 *  switch (see `utils/nShowcase`). */
export const selectNShowcase = (state: RootState): boolean => selectNShowcasePref(state) || N_SHOWCASE_LOCAL;

/** The persisted preferred UI language code (e.g. 'en' / 'mx'), or `null`. */
export const selectLanguagePref = (state: RootState): string | null =>
  (state.userPreferences.preferences[PREFERENCE_KEYS.language] as string | undefined) ?? null;

/**
 * The byline to prefill a new blog article with, or `null` if none was saved.
 *
 * Memoized because {@link normalizeBlogAuthorPrefs} builds a fresh object: a
 * plain selector returning one would compare unequal on every dispatched action
 * and re-render its subscriber each time.
 */
export const selectBlogAuthorPref = createSelector(
  [(state: RootState): unknown => state.userPreferences.preferences[PREFERENCE_KEYS.blogAuthor]],
  (saved): BlogAuthor | null => normalizeBlogAuthorPrefs(saved),
);

export default userPreferencesSlice.reducer;
